import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { 
  Container, 
  Grid, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Switch, 
  FormControlLabel, 
  Paper, 
  Divider, 
  CircularProgress, 
  Autocomplete,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  InputAdornment,
  Chip,
  Tooltip,
  IconButton,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import HotelIcon from '@mui/icons-material/Hotel';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import PersonIcon from '@mui/icons-material/Person';
import WifiIcon from '@mui/icons-material/Wifi';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ImageIcon from '@mui/icons-material/Image';
import StarIcon from '@mui/icons-material/Star';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PoolIcon from '@mui/icons-material/Pool';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';
import SettingsIcon from '@mui/icons-material/Settings';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import InventoryIcon from '@mui/icons-material/Inventory';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Security from '@mui/icons-material/Security';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import PeopleIcon from '@mui/icons-material/People';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';
import { account, updateAccount, verifyGst } from '../api/authHandlers';
import AMENITIES_OPTIONS from '../config/amenitiesOptions';

const SECRET_KEY = 'tabtrips_hotel_secret';

const HOTEL_POLICY_OPTIONS = [
  'Valid government ID required at check-in',
  'No smoking inside rooms',
  'Pets not allowed',
  'Outside food allowed',
  'Outside food not allowed',
  'Loud music not allowed after 10 PM',
  'Visitors allowed till 10 PM',
  'Early check-in subject to availability',
  'Late check-out subject to availability',
  'Local ID accepted',
  'Unmarried couples allowed',
  'Children below 6 stay free without extra bed',
  'Extra bed available at additional cost',
  'Free Cancellation',
  'Non-Refundable',
  'Pet Friendly',
  'No Smoking',
];

const CANCELLATION_POLICY_OPTIONS = [
  'Flexible',
  'Moderate',
  'Strict',
  'Non-Refundable',
  'Free cancellation up to 24 hours before check-in',
  'Free cancellation up to 48 hours before check-in',
  'Non-refundable booking',
];

function parseYesNoFlag(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  const s = String(value ?? '').trim().toLowerCase();
  return s === '1' || s === 'yes' || s === 'true';
}

function splitPolicyList(value) {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  return String(value || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}
const TOKEN_COOKIE_KEY = 'token';
function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}
function removeToken() {
  Cookies.remove(TOKEN_COOKIE_KEY);
}

function decryptData(ciphertext) {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  } catch {
    return null;
  }
}

const getGstStatusNormalized = (status) => String(status || '').toLowerCase().trim();

const GstVerificationStatus = ({ status }) => {
  const normalized = getGstStatusNormalized(status);

  if (normalized === 'pending') {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        <Typography variant="body2">
          GST verification is pending. It will be verified within 24–48 hours.
        </Typography>
      </Alert>
    );
  }

  if (normalized === 'rejected') {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        <Typography variant="body2">
          We could not verify your GST number. Please review the details entered. If you believe this is a mistake, kindly contact TabTrips support.
        </Typography>
      </Alert>
    );
  }

  return null;
};

const getGstFieldAdornment = (status) => {
  const normalized = getGstStatusNormalized(status);

  if (normalized === 'active') {
    return (
      <InputAdornment position="end">
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: '16px !important' }} />}
          label="Verified"
          size="small"
          color="success"
          sx={{ fontWeight: 600, height: 24 }}
        />
      </InputAdornment>
    );
  }

  if (normalized === 'pending') {
    return (
      <InputAdornment position="end">
        <Chip label="Pending" size="small" color="warning" sx={{ fontWeight: 600, height: 24 }} />
      </InputAdornment>
    );
  }

  if (normalized === 'rejected') {
    return (
      <InputAdornment position="end">
        <Chip label="Rejected" size="small" color="error" sx={{ fontWeight: 600, height: 24 }} />
      </InputAdornment>
    );
  }

  return null;
};

const Settings = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [hotelTimeLoading, setHotelTimeLoading] = useState(false);
  const [checkInOutSaving, setCheckInOutSaving] = useState(false);
  const checkInOutSaveTimer = useRef(null);
  const [tabValue, setTabValue] = useState(0);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');
  
  // Sub Host Assignment State
  const [subHostLoading, setSubHostLoading] = useState(false);
  const [subHostFormData, setSubHostFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gender: '',
    password: '',
    confirmPassword: '',
    role: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [allSubHosts, setAllSubHosts] = useState([]);
  
  // OTP System State
  const [otpVerified, setOtpVerified] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpInput, setOtpInput] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [bankingEditable, setBankingEditable] = useState(false);
  
  // Banking Details State
  const [bankingDetails, setBankingDetails] = useState({
    account_holder_name: '',
    account_number: '',
    bank_name: '',
    ifsc_code: '',
    pan_number: '',
    gst_number: '',
    gst_active: '',
    postal_code: ''
  });
  const [reEnterAccountNumber, setReEnterAccountNumber] = useState('');
  const [accountDetailsLoading, setAccountDetailsLoading] = useState(false);
  const [gstVerifying, setGstVerifying] = useState(false);
  const [gstVerifiedData, setGstVerifiedData] = useState(null);
  const [gstError, setGstError] = useState('');
  const [gstLocked, setGstLocked] = useState(false);
  
  // Account Proof State
  const [accountProofFile, setAccountProofFile] = useState(null);
  const [accountProofPreview, setAccountProofPreview] = useState(null);
  const [idProofFile, setIdProofFile] = useState(null);
  const [idProofPreview, setIdProofPreview] = useState(null);
  const [accountProofLoading, setAccountProofLoading] = useState(false);
  
  // Amenities list shared with the listing/onboarding flow (single source of truth)
  const amenitiesList = AMENITIES_OPTIONS;

  const [formData, setFormData] = useState({
    // Basic Hotel Information
    hotel_id: '',
    hotel_name: '',
    description: '',
    type: '',
    rating: '',
    isActive: true,
    open_24_7: true,
    is_couple_allowed: true,
    room_count: '',
    
    // Contact Information
    email: '',
    phone: '',
    address: '',
    location: '',
    location_point: '',
    city_id: '',
    state_id: '',
    country_id: '',
    
    // Owner Information
    hotel_owner_full_name: '',
    hotel_owner_mobile_number: '',
    
    // Legal Information
    aadhar_card_number: '',
    gst_number: '', 
    pan_number: '',
    
    // Bank Details
    account_holder_name: '',
    account_number: '',
    bank_name: '',
    ifsc_code: '',
    postal_code: '',
    
    // Property Settings
    amenities: [],
    check_in_time: '',
    check_out_time: '',
    cancellation_policy: '',
    hotel_policy: '',
    hotel_percentage: '',
    payment_mode: '',
  });

  // Add these state variables at the top with other useState declarations
  const [selectedHotelId, setSelectedHotelId] = useState(localStorage.getItem('selectedHotelId') || '');
  const [selectedHostId, setSelectedHostId] = useState(localStorage.getItem('selectedHostId') || '');
  

  // Add these state options near the top of the component, before the return statement
  const stateOptions = [
    { id: '14', name: 'Uttarakhand' }
  ];

  const cityOptions = [
    { id: '19', name: 'Rudraprayag' }
  ];

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [amenitiesDropdownOpen, setAmenitiesDropdownOpen] = useState(false);

  const hotelIdRef = useRef(selectedHotelId);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "selectedHotelId") {
        const newHotelId = localStorage.getItem("selectedHotelId") || '';
        if (newHotelId !== hotelIdRef.current) {
          hotelIdRef.current = newHotelId;
          window.location.reload();
        }
    }
  };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    if (selectedHotelId !== hotelIdRef.current) {
      hotelIdRef.current = selectedHotelId;
      window.location.reload();
    }
  }, [selectedHotelId]);

  // Modify the useEffect to fetch states and cities
  useEffect(() => {
    const loadHotelDetails = async () => {
      try {
        setLoading(true);
        setLoadingLocations(true);
        
        // Get hotelList and selectedHotelId from localStorage
        const hotelListStr = localStorage.getItem('hotelsList');
        const selectedHotelId = localStorage.getItem('selectedHotelId');
        

        if (!hotelListStr) {
          setLoading(false);
          return;
        }

        // Parse hotelList and find selected hotel
        const hotelList = decryptData(hotelListStr);

        // If no selectedHotelId, use the first hotel in the list
        const hotelData = selectedHotelId 
          ? hotelList.find(hotel => hotel.hotel_id.toString() === selectedHotelId)
          : hotelList[0];

        // console.log("Selected hotel data:", hotelData);

        if (!hotelData) {
          // console.log("No hotel data found");
          setLoading(false);
          return;
        }

        // Convert amenities string to array
        const amenitiesArray = hotelData.amenities 
          ? hotelData.amenities.split(', ') 
          : [];
      
        // Update form data with fetched data
        setFormData({
          hotel_id: hotelData.hotel_id || '',
          hotel_name: hotelData.hotel_name || '',
          description: hotelData.description || '',
          type: hotelData.type || '',
          rating: hotelData.rating || '',
          isActive: Boolean(hotelData.isActive),
          open_24_7: parseYesNoFlag(hotelData.open_24_7),
          is_couple_allowed: parseYesNoFlag(hotelData.is_couple_allowed),
          room_count: hotelData.room_count || '',
          email: hotelData.email || '',
          phone: hotelData.phone || '',
          address: hotelData.address || '',
          location: hotelData.location || '',
          location_point: hotelData.location_point || '',
          city_id: hotelData.city_id || '',
          state_id: hotelData.state_id || '',
          country_id: hotelData.country_id || '',
          hotel_owner_full_name: hotelData.hotel_owner_full_name || '',
          hotel_owner_mobile_number: hotelData.hotel_owner_mobile_number || '',
          aadhar_card_number: hotelData.aadhar_card_number || '',
          gst_number: hotelData.gst_number || '',
          pan_number: hotelData.pan_number || '',
          account_holder_name: hotelData.account_holder_name || '',
          account_number: hotelData.account_number || '',
          bank_name: hotelData.bank_name || '',
          ifsc_code: hotelData.ifsc_code || '',
          postal_code: hotelData.postal_code || '',
          amenities: amenitiesArray,
          check_in_time: hotelData.check_in_time || '',
          check_out_time: hotelData.check_out_time || '',
          cancellation_policy: hotelData.cancellation_policy || '',
          hotel_policy: hotelData.hotel_policy || '',
          hotel_percentage: hotelData.hotel_percentage || '',
          payment_mode: hotelData.payment_mode || ''
        });

        // Fetch states and cities
        // await fetchStates();
        // if (hotelData.state_id) {
        //   await fetchCities(hotelData.state_id);
        // }
        
        // console.log("Form data set successfully");
        
      } catch (error) {
        console.error("Error in loadHotelDetails:", error);
        showAlert(error.message, 'error');
      } finally {
        setLoading(false);
        setLoadingLocations(false);
      }
    };
    
    loadHotelDetails();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle amenities change
  const handleAmenitiesChange = (event, newValue) => {
    setFormData((prevData) => ({
      ...prevData,
      amenities: newValue
    }));
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Show snackbar notification
  const showAlert = (message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
    
    // Immediately stop loading state for any error
    if (severity === 'error') {
      setSaveLoading(false);
    }
  };

  // Close snackbar
  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  // OTP Functions
  const generateOTP = async () => {
    const token = getDecryptedToken();
    const hotelId = localStorage.getItem('selectedHotelId');
    
    if (!token) {
      showAlert('Authentication token not found. Please login again.', 'error');
      return;
    }
    
    if (!hotelId) {
      showAlert('Hotel ID not found. Please select a hotel first.', 'error');
      return;
    }
    
    if (!formData.email) {
      showAlert('Email not found. Please check your hotel details.', 'error');
      return;
    }
    
    setOtpLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('email', formData.email);
      
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/generateOTP',
        formDataToSend,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          }
        }
      );
      
      if (response.data.status === true) {
        setOtpSent(true);
        setShowOtpInput(true);
        showAlert('✅ Verification code sent successfully!', 'success');
      } else {
        showAlert(response.data.message || 'Failed to send verification code. Please try again.', 'error');
      }
    } catch (error) {
      console.error('OTP Generation Error:', error);
      showAlert('❌ Failed to send verification code. Please check your connection and try again.', 'error');
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otpInput.trim()) {
      showAlert('Please enter the verification code.', 'error');
      return;
    }
    
    if (otpInput.length !== 6) {
      showAlert('Please enter a complete 6-digit verification code.', 'error');
      return;
    }
    
    setOtpLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('email', formData.email);
      formDataToSend.append('otp', otpInput);
      
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/verifyOtp',
        formDataToSend,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          }
        }
      );
      
      if (response.data.status === true) {
        setOtpVerified(true);
        setBankingEditable(true);
        setShowOtpInput(false);
        setOtpInput('');
        setReEnterAccountNumber(''); // Reset re-enter account number when entering edit mode
        showAlert('✅ Identity verified successfully! You can now edit banking details.', 'success');
      } else {
        showAlert('❌ Invalid verification code. Please check and try again.', 'error');
      }
    } catch (error) {
      console.error('OTP Verification Error:', error);
      showAlert('❌ Verification failed. Please check your connection and try again.', 'error');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleBankingDetailsChange = (e) => {
    const { name, value } = e.target;
    setBankingDetails(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'gst_number' && gstLocked) {
      setGstLocked(false);
      setGstVerifiedData(null);
      setGstError('');
      setBankingDetails(prev => ({ ...prev, gst_active: '' }));
    }
  };

  const handleVerifyGstSettings = async () => {
    const gstin = (bankingDetails.gst_number || '').trim().toUpperCase();
    if (!gstin) {
      setGstError('Please enter a GST number first');
      return;
    }
    setGstVerifying(true);
    setGstError('');
    setGstVerifiedData(null);
    try {
      const result = await verifyGst(gstin);
      if (result.success && result.data) {
        if (result.data.status === 'Active') {
          setGstVerifiedData(result.data);
          setGstLocked(true);
          setBankingDetails(prev => ({
            ...prev,
            gst_number: gstin,
            gst_active: 'active',
            pan_number: result.data.pan || prev.pan_number,
          }));
          showAlert('GST verified successfully. PAN auto-filled.', 'success');
        } else {
          setGstError(`GST status is "${result.data.status}" — only Active GST numbers are accepted`);
        }
      } else {
        setGstError(result.message || 'Failed to verify GST number');
      }
    } catch (err) {
      setGstError(err?.response?.data?.message || 'Failed to verify GST number');
    } finally {
      setGstVerifying(false);
    }
  };

  const handleBankingDetailsSave = async () => {
    const token = getDecryptedToken();
    const hotelId = localStorage.getItem('selectedHotelId');
    
    if (!token) {
      showAlert('Authentication token not found. Please login again.', 'error');
      return;
    }
    
    if (!hotelId) {
      showAlert('Hotel ID not found. Please select a hotel first.', 'error');
      return;
    }
    
    // Validation
    if (!bankingDetails.account_holder_name || !bankingDetails.account_holder_name.trim()) {
      showAlert('Please enter account holder name.', 'error');
      return;
    }
    
    if (!bankingDetails.account_number || !bankingDetails.account_number.trim()) {
      showAlert('Please enter account number.', 'error');
      return;
    }
    
    if (bankingDetails.account_number.length < 6) {
      showAlert('Enter complete account number', 'error');
      return;
    }
    
    if (bankingDetails.account_number !== reEnterAccountNumber) {
      showAlert('Account numbers do not match. Please re-enter.', 'error');
      return;
    }
    
    if (!bankingDetails.bank_name || !bankingDetails.bank_name.trim()) {
      showAlert('Please enter bank name.', 'error');
      return;
    }
    
    if (!bankingDetails.ifsc_code || !bankingDetails.ifsc_code.trim()) {
      showAlert('Please enter IFSC code.', 'error');
      return;
    }
    
    if (!bankingDetails.pan_number || !bankingDetails.pan_number.trim()) {
      showAlert('Please enter PAN number.', 'error');
      return;
    }
    
    if (!bankingDetails.postal_code || !bankingDetails.postal_code.trim()) {
      showAlert('Please enter postal code.', 'error');
      return;
    }
    
    setSaveLoading(true);
    try {
      const payload = {
        hotel_id: parseInt(hotelId),
        token: token,
        account_holder_name: bankingDetails.account_holder_name.trim(),
        account_number: bankingDetails.account_number.trim(),
        bank_name: bankingDetails.bank_name.trim(),
        ifsc_code: bankingDetails.ifsc_code.trim(),
        pan_number: bankingDetails.pan_number.trim(),
        gst_number: bankingDetails.gst_number ? bankingDetails.gst_number.trim() : '',
        gst_active: (gstLocked || getGstStatusNormalized(bankingDetails.gst_active) === 'active')
          ? 'active'
          : (bankingDetails.gst_active || ''),
        postal_code: bankingDetails.postal_code.trim()
      };
      
      const response = await updateAccount(payload);
      
      if (response.status === true) {
        showAlert('✅ Account details updated successfully!', 'success');
        setBankingEditable(false);
        setOtpVerified(false);
        setReEnterAccountNumber('');
        // Update the main form data
        setFormData(prev => ({
          ...prev,
          account_holder_name: bankingDetails.account_holder_name,
          account_number: bankingDetails.account_number,
          bank_name: bankingDetails.bank_name,
          ifsc_code: bankingDetails.ifsc_code,
          pan_number: bankingDetails.pan_number,
          gst_number: bankingDetails.gst_number,
          gst_active: (gstLocked || getGstStatusNormalized(bankingDetails.gst_active) === 'active')
            ? 'active'
            : (bankingDetails.gst_active || ''),
          postal_code: bankingDetails.postal_code
        }));
        // Refresh account details from API
        await fetchAccountDetails();
      } else {
        showAlert(response.message || 'Failed to update account details. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Account Details Update Error:', error);
      showAlert('❌ Failed to update account details. Please check your connection and try again.', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleBankingDetailsCancel = () => {
    setBankingDetails({
      account_holder_name: formData.account_holder_name || '',
      account_number: formData.account_number || '',
      bank_name: formData.bank_name || '',
      ifsc_code: formData.ifsc_code || '',
      pan_number: formData.pan_number || '',
      gst_number: formData.gst_number || '',
      gst_active: formData.gst_active || '',
      postal_code: formData.postal_code || ''
    });
    setReEnterAccountNumber('');
    setBankingEditable(false);
    setOtpVerified(false);
    setShowOtpInput(false);
    setOtpInput('');
    setGstVerifying(false);
    setGstVerifiedData(null);
    setGstError('');
    setGstLocked(false);
    setOtpSent(false);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset any previous error states
    setAlertOpen(false);
    setAlertMessage('');
    setSaveLoading(true);
    
    // Frontend validation
    if (!formData.hotel_id) {
      setSaveLoading(false);
      showAlert("Hotel ID is missing", "error");
      return;
    }

    if (!formData.amenities || formData.amenities.length === 0) {
      setSaveLoading(false);
      showAlert("Please select at least one amenity", "error");
      return;
    }
    
    try {
      // Get token from cookies
      const token = getDecryptedToken();
      if (!token) {
        setSaveLoading(false);
        throw new Error('Authentication token not found. Please login again.');
      }

      // Only update amenities
      const response = await axios.post('https://api.tabtrips.com/olivrweb/hotelapi/update.php?function=updateHotelAmenities', {
        hotel_id: formData.hotel_id,
        amenities: formData.amenities.join(', '),
        token: token
      }).catch(error => {
        // Handle axios specific errors
        setSaveLoading(false);
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          throw new Error(error.response.data.message || "Server error occurred");
        } else if (error.request) {
          // The request was made but no response was received
          throw new Error("No response from server. Please check your internet connection");
        } else {
          // Something happened in setting up the request that triggered an Error
          throw new Error("Failed to make request. Please try again");
        }
      });
      
      // Check if response exists and has data
      if (!response || !response.data) {
        setSaveLoading(false);
        throw new Error("Invalid response from server");
      }

      // Check response status
      if (!response.data.status) {
        setSaveLoading(false);
        throw new Error(response.data.message || "Failed to update amenities");
      }

      // Success case
      showAlert("Amenities updated successfully!", "success");
      
      // Update the hotelList in localStorage with new amenities
      try {
        const hotelListStr = localStorage.getItem('hotelsList');
        if (hotelListStr) {
          const hotelList = decryptData(hotelListStr);
          const updatedHotelList = hotelList.map(hotel => {
            if (hotel.hotel_id === formData.hotel_id) {
              return {
                ...hotel,
                amenities: formData.amenities.join(', ')
              };
            }
            return hotel;
          });
          // Re-encrypt before saving
          localStorage.setItem('hotelsList', CryptoJS.AES.encrypt(JSON.stringify(updatedHotelList), SECRET_KEY).toString());
        }
      } catch (storageError) {
        console.error("Error updating localStorage:", storageError);
        // Don't show error to user as this is not critical
      }

    } catch (error) {
      console.error("Error saving amenities:", error);
      setSaveLoading(false);
      
      // Handle different types of errors
      let errorMessage = "Failed to save amenities. Please try again.";
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      showAlert(errorMessage, "error");
      
      // Handle token related errors
      if (errorMessage.toLowerCase().includes('token') || 
          errorMessage.toLowerCase().includes('auth') || 
          errorMessage.toLowerCase().includes('login')) {
        removeToken();
        navigate('/login');
      }
    } finally {
      // Ensure loading state is false in all cases
      setSaveLoading(false);
    }
  };

  // Handle sub host assignment submit
  const handleSubHostSubmit = async () => {
    // Validation
    if (!subHostFormData.name || !subHostFormData.name.trim()) {
      showAlert('Please enter name', 'error');
      return;
    }
    
    if (!subHostFormData.email || !subHostFormData.email.trim()) {
      showAlert('Please enter email', 'error');
      return;
    }
    
    if (!subHostFormData.phone || !subHostFormData.phone.trim()) {
      showAlert('Please enter phone', 'error');
      return;
    }
    
    if (!subHostFormData.gender) {
      showAlert('Please select gender', 'error');
      return;
    }
    
    if (!subHostFormData.password || !subHostFormData.password.trim()) {
      showAlert('Please enter password', 'error');
      return;
    }
    
    if (subHostFormData.password !== subHostFormData.confirmPassword) {
      showAlert('Passwords do not match', 'error');
      return;
    }
    
    if (!subHostFormData.role) {
      showAlert('Please select a role/permission', 'error');
      return;
    }
    
    // Get token
    const token = getDecryptedToken();
    if (!token) {
      showAlert('Authentication token not found. Please login again.', 'error');
      return;
    }
    
    setSubHostLoading(true);
    
    try {
      // Set role permissions - selected role = 1, others = 0
      const inventory_manage = subHostFormData.role === 'inventory_manage' ? '1' : '0';
      const booking_manage = subHostFormData.role === 'booking_manage' ? '1' : '0';
      const hotel_manage = subHostFormData.role === 'hotel_manage' ? '1' : '0';
      const view_only = subHostFormData.role === 'view_only' ? '1' : '0';
      
      // Create FormData
      const formDataToSend = new FormData();
      formDataToSend.append('token', token);
      formDataToSend.append('name', subHostFormData.name.trim());
      formDataToSend.append('email', subHostFormData.email.trim());
      formDataToSend.append('phone', subHostFormData.phone.trim());
      formDataToSend.append('gender', subHostFormData.gender);
      formDataToSend.append('password', subHostFormData.password);
      formDataToSend.append('confirmpassword', subHostFormData.confirmPassword);
      formDataToSend.append('inventory_manage', inventory_manage);
      formDataToSend.append('booking_manage', booking_manage);
      formDataToSend.append('hotel_manage', hotel_manage);
      formDataToSend.append('view_only', view_only);
      
      // Send to API
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/setSubHostDetails',
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );
      
      if (response.data && response.data.status === true) {
        showAlert('✅ User assigned successfully!', 'success');
        // Reset form
        setSubHostFormData({
          name: '',
          email: '',
          phone: '',
          gender: '',
          password: '',
          confirmPassword: '',
          role: ''
        });
        // Reload sub hosts list
        try {
          const allSubHostsStr = localStorage.getItem('allSubHosts');
          if (allSubHostsStr) {
            const decrypted = decryptData(allSubHostsStr);
            if (decrypted && Array.isArray(decrypted)) {
              setAllSubHosts(decrypted);
            }
          }
        } catch (error) {
          console.error('Error reloading sub hosts:', error);
        }
      } else {
        showAlert(response.data?.message || 'Failed to assign user. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Sub Host Assignment Error:', error);
      
      let errorMessage = 'Failed to assign user. Please check your connection and try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showAlert(errorMessage, 'error');
      
      // Handle token related errors
      if (errorMessage.toLowerCase().includes('token') || 
          errorMessage.toLowerCase().includes('auth') || 
          errorMessage.toLowerCase().includes('login')) {
        removeToken();
        navigate('/login');
      }
    } finally {
      setSubHostLoading(false);
    }
  };

  const yesNoToBool = (value) => {
    if (typeof value === 'boolean') return value;
    const v = String(value ?? '').trim().toLowerCase();
    return v === 'yes' || v === '1' || v === 'true';
  };

  const normalizeTimeForInput = (value) => {
    if (!value) return '';
    const s = String(value).trim();
    // Accept HH:MM or HH:MM:SS → keep HH:MM for <input type="time">
    const m = s.match(/^(\d{1,2}:\d{2})(?::\d{2})?$/);
    return m ? m[1].padStart(5, '0') : s;
  };

  // Check-in / check-out → setCheckInCheckOut (on change, debounced)
  const saveCheckInCheckOut = useCallback(async (checkIn, checkOut, hotelId) => {
    if (!checkIn || !checkOut || !hotelId) return;
    const token = getDecryptedToken();
    if (!token) {
      showAlert('Authentication token not found. Please login again.', 'error');
      return;
    }
    setCheckInOutSaving(true);
    try {
      const timeResponse = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/setCheckInCheckOut',
        {
          token,
          hotel_id: hotelId.toString(),
          check_in: checkIn,
          check_out: checkOut,
        }
      );
      if (!timeResponse.data?.status) {
        throw new Error(timeResponse.data?.message || 'Failed to update check-in / check-out');
      }
      const saved = timeResponse.data?.data || {};
      if (saved.check_in || saved.check_out) {
        setFormData((prev) => ({
          ...prev,
          check_in_time: normalizeTimeForInput(saved.check_in) || prev.check_in_time,
          check_out_time: normalizeTimeForInput(saved.check_out) || prev.check_out_time,
        }));
      }
      showAlert(timeResponse.data?.message || 'Check-in / check-out updated', 'success');
    } catch (error) {
      console.error('Error saving check-in/out:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to update check-in / check-out';
      showAlert(errorMessage, 'error');
      if (
        errorMessage.toLowerCase().includes('token') ||
        errorMessage.toLowerCase().includes('auth') ||
        errorMessage.toLowerCase().includes('login')
      ) {
        removeToken();
        navigate('/login');
      }
    } finally {
      setCheckInOutSaving(false);
    }
  }, [navigate]);

  const handleCheckInOutChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => {
      const next = { ...prevData, [name]: value };
      if (checkInOutSaveTimer.current) clearTimeout(checkInOutSaveTimer.current);
      checkInOutSaveTimer.current = setTimeout(() => {
        saveCheckInCheckOut(next.check_in_time, next.check_out_time, next.hotel_id);
      }, 400);
      return next;
    });
  };

  useEffect(() => {
    return () => {
      if (checkInOutSaveTimer.current) clearTimeout(checkInOutSaveTimer.current);
    };
  }, []);

  // Policies only → updateHotelPolicies (Save button) — never send check_in / check_out
  const handleSaveHotelTime = async (e) => {
    if (e) e.preventDefault();

    if (!formData.hotel_id) {
      showAlert('Hotel ID is missing', 'error');
      return;
    }

    if (!formData.cancellation_policy) {
      showAlert('Please select a cancellation policy', 'error');
      return;
    }

    if (!String(formData.hotel_policy || '').trim()) {
      showAlert('Please select at least one hotel policy', 'error');
      return;
    }

    setHotelTimeLoading(true);
    try {
      const token = getDecryptedToken();
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const policiesResponse = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/update.php?function=updateHotelPolicies',
        {
          token,
          hotel_id: formData.hotel_id,
          open_24_7: formData.open_24_7 ? 'Yes' : 'No',
          is_couple_allowed: formData.is_couple_allowed ? 'Yes' : 'No',
          cancellation_policy: formData.cancellation_policy,
          hotel_policy: Array.isArray(formData.hotel_policy)
            ? formData.hotel_policy.join(', ')
            : String(formData.hotel_policy || ''),
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (!policiesResponse.data?.status) {
        throw new Error(policiesResponse.data?.message || 'Failed to update hotel policies');
      }

      const saved = policiesResponse.data?.data || {};
      setFormData((prev) => ({
        ...prev,
        open_24_7: yesNoToBool(
          saved.open_24_7_label ?? saved.open_24_7 ?? prev.open_24_7
        ),
        is_couple_allowed: yesNoToBool(
          saved.is_couple_allowed_label ?? saved.is_couple_allowed ?? prev.is_couple_allowed
        ),
        cancellation_policy:
          saved.cancellation_policy != null
            ? String(saved.cancellation_policy)
            : prev.cancellation_policy,
        hotel_policy:
          saved.hotel_policy != null ? String(saved.hotel_policy) : prev.hotel_policy,
      }));

      showAlert(policiesResponse.data?.message || 'Hotel policies updated successfully!', 'success');
    } catch (error) {
      console.error('Error saving hotel policies:', error);

      let errorMessage = 'Failed to save hotel policies. Please try again.';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      showAlert(errorMessage, 'error');

      if (
        errorMessage.toLowerCase().includes('token') ||
        errorMessage.toLowerCase().includes('auth') ||
        errorMessage.toLowerCase().includes('login')
      ) {
        removeToken();
        navigate('/login');
      }
    } finally {
      setHotelTimeLoading(false);
    }
  };

  // Define animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.4 }
    }
  };

  const handleAdminAccess = () => {
    navigate('/partner_registrationform?key=tabtrips2024');
  };

  // Add handler for state change
  const handleStateChange = async (e) => {
    const stateId = e.target.value;
    handleInputChange(e);
    if (stateId) {
      setLoadingLocations(true);
      // await fetchCities(stateId);
      setLoadingLocations(false);
    } else {
      setCities([]);
    }
  };

  // Fetch account details from API
  const fetchAccountDetails = useCallback(async () => {
    const token = getDecryptedToken();
    const hotelId = localStorage.getItem('selectedHotelId');
    
    if (!token || !hotelId) {
      return;
    }
    
    setAccountDetailsLoading(true);
    try {
      const payload = {
        hotel_id: parseInt(hotelId),
        token: token
      };
      
      const response = await account(payload);
      
      if (response.status === true && response.account_details) {
        const accountData = response.account_details;
        setBankingDetails({
          account_holder_name: accountData.account_holder_name || '',
          account_number: accountData.account_number || '',
          bank_name: accountData.bank_name || '',
          ifsc_code: accountData.ifsc_code || '',
          pan_number: accountData.pan_number || '',
          gst_number: accountData.gst_number || '',
          gst_active: accountData.gst_active || '',
          postal_code: accountData.postal_code || ''
        });
        
        // Update formData as well for display
        setFormData(prev => ({
          ...prev,
          account_holder_name: accountData.account_holder_name || '',
          account_number: accountData.account_number || '',
          bank_name: accountData.bank_name || '',
          ifsc_code: accountData.ifsc_code || '',
          pan_number: accountData.pan_number || '',
          gst_number: accountData.gst_number || '',
          postal_code: accountData.postal_code || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching account details:', error);
      // Don't show error to user, just log it
    } finally {
      setAccountDetailsLoading(false);
    }
  }, []);

  // Fetch account details when financial tab is opened
  useEffect(() => {
    if (tabValue === 2) {
      fetchAccountDetails();
    }
  }, [tabValue, fetchAccountDetails]);

  // Initialize banking details when formData loads (fallback)
  useEffect(() => {
    if (formData.account_holder_name && !accountDetailsLoading) {
      setBankingDetails({
        account_holder_name: formData.account_holder_name,
        account_number: formData.account_number,
        bank_name: formData.bank_name,
        ifsc_code: formData.ifsc_code,
        pan_number: formData.pan_number || '',
        gst_number: formData.gst_number || '',
        postal_code: formData.postal_code || ''
      });
    }
  }, [formData.account_holder_name, formData.account_number, formData.bank_name, formData.ifsc_code, formData.pan_number, formData.gst_number, formData.postal_code]);

  // Reset OTP state when component mounts (page refresh)
  useEffect(() => {
    setOtpVerified(false);
    setBankingEditable(false);
    setShowOtpInput(false);
    setOtpInput('');
    setOtpSent(false);
  }, []);

  // Load allSubHosts from localStorage
  useEffect(() => {
    const loadSubHosts = () => {
      try {
        const allSubHostsStr = localStorage.getItem('allSubHosts');
        if (allSubHostsStr) {
          const decrypted = decryptData(allSubHostsStr);
          if (decrypted && Array.isArray(decrypted)) {
            setAllSubHosts(decrypted);
          }
        }
      } catch (error) {
        console.error('Error loading sub hosts:', error);
      }
    };
    
    loadSubHosts();
    
    // Listen for hotelsUpdated event to reload sub hosts
    const handleHotelsUpdated = () => {
      loadSubHosts();
    };
    
    window.addEventListener('hotelsUpdated', handleHotelsUpdated);
    return () => window.removeEventListener('hotelsUpdated', handleHotelsUpdated);
  }, []);

  // Reload sub hosts when Assign User tab is opened
  useEffect(() => {
    if (tabValue === 5) {
      try {
        const allSubHostsStr = localStorage.getItem('allSubHosts');
        if (allSubHostsStr) {
          const decrypted = decryptData(allSubHostsStr);
          if (decrypted && Array.isArray(decrypted)) {
            setAllSubHosts(decrypted);
          }
        }
      } catch (error) {
        console.error('Error loading sub hosts:', error);
      }
    }
  }, [tabValue]);

  // Handle account proof file selection
  const handleAccountProofChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showAlert('Please select an image file for account proof', 'error');
        return;
      }
      setAccountProofFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAccountProofPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle ID proof file selection
  const handleIdProofChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showAlert('Please select an image file for ID proof', 'error');
        return;
      }
      setIdProofFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setIdProofPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle account proof submission
  const handleAccountProofSubmit = async () => {
    const hotelId = localStorage.getItem('selectedHotelId');
    
    if (!hotelId) {
      showAlert('Hotel ID not found. Please select a hotel first.', 'error');
      return;
    }
    
    if (!accountProofFile) {
      showAlert('Please upload account proof', 'error');
      return;
    }
    
    if (!idProofFile) {
      showAlert('Please upload ID proof', 'error');
      return;
    }
    
    setAccountProofLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('hotel_id', hotelId);
      formDataToSend.append('account_proof', accountProofFile);
      formDataToSend.append('id_proof', idProofFile);
      
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/setHotelAccountProof',
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );
      
      if (response.data && response.data.status === true) {
        showAlert('Account proof uploaded successfully!', 'success');
        // Clear the files and previews after successful upload
        setAccountProofFile(null);
        setIdProofFile(null);
        setAccountProofPreview(null);
        setIdProofPreview(null);
        // Reset file inputs
        const accountProofInput = document.getElementById('account-proof-input');
        const idProofInput = document.getElementById('id-proof-input');
        if (accountProofInput) accountProofInput.value = '';
        if (idProofInput) idProofInput.value = '';
      } else {
        showAlert(response.data?.message || 'Failed to upload account proof. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Account Proof Upload Error:', error);
      let errorMessage = 'Failed to upload account proof. Please check your connection and try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showAlert(errorMessage, 'error');
    } finally {
      setAccountProofLoading(false);
    }
  };

  // Remove account proof preview
  const removeAccountProof = () => {
    setAccountProofFile(null);
    setAccountProofPreview(null);
    const input = document.getElementById('account-proof-input');
    if (input) input.value = '';
  };

  // Remove ID proof preview
  const removeIdProof = () => {
    setIdProofFile(null);
    setIdProofPreview(null);
    const input = document.getElementById('id-proof-input');
    if (input) input.value = '';
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '80vh' 
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" ml={2}>Loading settings...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, position: 'relative', minHeight: '100vh' }}>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header */}
        <motion.div variants={itemVariants}>
          <Box 
            sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center', 
            mb: 4,
            flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 2 : 0,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -16,
                left: 0,
                width: '100%',
                height: '1px',
                background: 'linear-gradient(90deg, rgba(255,119,0,0.5) 0%, rgba(255,119,0,0.1) 100%)',
              }
            }}
          >
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: '#2E4057',
                fontWeight: 700,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                '& .MuiSvgIcon-root': {
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                  color: '#FF7700',
                  mr: 1.5
                }
              }}
            >
              <SettingsIcon />
              Hotel Settings
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 2,
              '& .MuiButton-root': {
                borderRadius: '12px',
                px: 3,
                py: 1.2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                }
              }
            }}>
            {/* <Button
              component={motion.button}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              variant="contained"
              color="primary"
              startIcon={<ImageIcon />}
              onClick={() => navigate(`/add-image?hotel_id=${formData.hotel_id}`)}
                sx={{
                  background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                  }
                }}
            >
              Manage Images
            </Button>
              <Button
                component={motion.button}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                variant="contained"
                color="primary"
                startIcon={<InventoryIcon />}
                onClick={() => navigate(`/rates-inventory/${formData.hotel_id}`)}
                sx={{
                  background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                  }
                }}
              >
                Manage Inventory
              </Button> */}
            </Box>
          </Box>
        </motion.div>

        {/* Settings Tabs */}
        <motion.div variants={itemVariants}>
          <Box sx={{ 
            mb: 4,
            '& .MuiTabs-root': {
              minHeight: '60px',
              borderBottom: '1px solid rgba(0,0,0,0.08)',
            },
            '& .MuiTab-root': {
              minHeight: '60px',
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
              color: '#2E4057',
              opacity: 0.7,
              transition: 'all 0.3s ease',
              '&.Mui-selected': {
                color: '#FF7700',
                opacity: 1,
                fontWeight: 600,
                '& .MuiSvgIcon-root': {
                  color: '#FF7700',
                }
              },
              '& .MuiSvgIcon-root': {
                fontSize: '1.3rem',
                marginBottom: '4px',
                color: '#2E4057',
                opacity: 0.7,
                transition: 'all 0.3s ease',
              }
            },
            '& .MuiTabs-indicator': {
              height: '3px',
              borderRadius: '3px 3px 0 0',
              background: 'linear-gradient(90deg, #FF7700, #FF9D4D)',
            }
          }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons={isMobile ? "auto" : false}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab icon={<HotelIcon />} label="Basic Info" />
              <Tab icon={<LocationOnIcon />} label="Location" />
              {/* <Tab icon={<PersonIcon />} label="Owner Details" /> */}
              <Tab icon={<AccountBalanceIcon />} label="Financial" />
              <Tab icon={<WifiIcon />} label="Amenities" />
              <Tab icon={<LoyaltyIcon />} label="Policies" />
              <Tab icon={<PeopleIcon />} label="Assign User" />
            </Tabs>
          </Box>
        </motion.div>

        <form onSubmit={handleSubmit}>
          {/* Tab Panels Container */}
          <Box sx={{ 
            '& .MuiPaper-root': {
              borderRadius: '16px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
              border: '1px solid rgba(0,0,0,0.05)',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 6px 32px rgba(0,0,0,0.08)',
              }
            },
            '& .MuiTypography-h6': {
              fontSize: '1.1rem',
              fontWeight: 600,
              color: '#2E4057',
              display: 'flex',
              alignItems: 'center',
              '& .MuiSvgIcon-root': {
                color: '#FF7700',
                marginRight: '12px',
                fontSize: '1.4rem'
              }
            },
            '& .MuiTextField-root': {
              '& .MuiInputLabel-root': {
                color: '#2E4057',
                opacity: 0.8,
                '&.Mui-focused': {
                  color: '#FF7700',
                }
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FF7700',
                  }
                },
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FF7700',
                    borderWidth: '2px',
                  }
                }
              },
              '& .MuiInputAdornment-root': {
                '& .MuiSvgIcon-root': {
                  color: '#FF7700',
                }
              }
            },
            '& .MuiSwitch-root': {
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#FF7700',
                '& + .MuiSwitch-track': {
                  backgroundColor: '#FF7700',
                  opacity: 0.5,
                }
              }
            },
            '& .MuiChip-root': {
              borderRadius: '8px',
              backgroundColor: 'rgba(255,119,0,0.1)',
              color: '#FF7700',
              '& .MuiChip-deleteIcon': {
                color: '#FF7700',
              }
            },
            '& .MuiAutocomplete-tag': {
              backgroundColor: 'rgba(255,119,0,0.1)',
              color: '#FF7700',
              borderRadius: '8px',
              '& .MuiSvgIcon-root': {
                color: '#FF7700',
              }
            }
          }}>
          {/* Tab 1: Basic Hotel Information */}
          {tabValue === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              variants={itemVariants}
            >
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <ApartmentIcon sx={{ mr: 1 }} />
                  Basic Hotel Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Hotel Name"
                      name="hotel_name"
                      value={formData.hotel_name}
                      onChange={handleInputChange}
                        disabled={true}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HotelIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Property Type"
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                        disabled={true}
                    >
                      <MenuItem value="hotel">Hotel</MenuItem>
                      <MenuItem value="apartment">Apartment</MenuItem>
                      <MenuItem value="villa">Villa</MenuItem>
                      <MenuItem value="homestay">Homestay</MenuItem>
                      <MenuItem value="resort">Resort</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Rating"
                      name="rating"
                      type="number"
                      value={formData.rating}
                      onChange={handleInputChange}
                        disabled={true}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <StarIcon color="primary" />
                          </InputAdornment>
                        ),
                        inputProps: { min: 0, max: 5, step: 0.1 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Room Count"
                      name="room_count"
                      type="number"
                      value={formData.room_count}
                      onChange={handleInputChange}
                        disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={handleInputChange}
                          name="isActive"
                          color="primary"
                            disabled={true}
                        />
                      }
                      label="Property Active"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Hotel Description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                        disabled={true}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                            <DescriptionIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </motion.div>
          )}

          {/* Tab 2: Location & Contact */}
          {tabValue === 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              variants={itemVariants}
            >
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <LocationOnIcon sx={{ mr: 1 }} />
                  Location & Contact Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      name="address"
                      value={formData.address}
                  onChange={handleInputChange}
                        disabled={true}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOnIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label="Country"
                      name="country_id"
                      value={formData.country_id}
                      onChange={handleInputChange}
                        disabled={true}
                    >
                      <MenuItem value={1}>India</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label="State"
                      name="state_id"
                        value={formData.state_id || ''}
                        onChange={handleStateChange}
                        disabled={true}
                      >
                        {states.map((state) => (
                          <MenuItem key={state.id} value={state.id}>
                            {state.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label="City"
                      name="city_id"
                        value={formData.city_id || ''}
                      onChange={handleInputChange}
                        disabled={true}
                      >
                        {cities.map((city) => (
                          <MenuItem key={city.id} value={city.id}>
                            {city.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                        disabled={true}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                        disabled={true}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  {/* Location Display Section */}
                  {formData.location && (
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <LocationOnIcon sx={{ mr: 1 }} />
                        Hotel Location
                      </Typography>
                      <Box sx={{ 
                        mb: 2,
                        border: '2px solid #ff9800',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        width: '100%',
                        height: { xs: '250px', sm: '300px', md: '350px' },
                        '& iframe': {
                          width: '100%',
                          height: '100%',
                          border: 'none'
                        }
                      }}>
                        <div 
                          dangerouslySetInnerHTML={{ __html: formData.location }}
                          style={{
                            width: '100%',
                            height: '100%'
                          }}
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </motion.div>
          )}

          {/* Tab 3: Owner Information */}
          {/* {tabValue === 2 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              variants={itemVariants}
            >
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1 }} />
                  Owner Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Owner's Full Name"
                      name="hotel_owner_full_name"
                      value={formData.hotel_owner_full_name}
                      onChange={handleInputChange}
                        disabled={true}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Owner's Mobile Number"
                      name="hotel_owner_mobile_number"
                      value={formData.hotel_owner_mobile_number}
                      onChange={handleInputChange}
                        disabled={true}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Aadhar Card Number"
                      name="aadhar_card_number"
                      value={formData.aadhar_card_number}
                      onChange={handleInputChange}
                        disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="GST Number"
                      name="gst_number"
                      value={formData.gst_number}
                      onChange={handleInputChange}
                        disabled={true}
                      placeholder="Enter GST number"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="PAN Number"
                      name="pan_number"
                      value={formData.pan_number}
                      onChange={handleInputChange}
                        disabled={true}
                      placeholder="Enter PAN number"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </motion.div>
          )} */}

          {/* Tab 4: Financial Information */}
          {tabValue === 2 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              variants={itemVariants}
            >
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccountBalanceIcon sx={{ mr: 1 }} />
                  Financial Information
                </Typography>
                  {!bankingEditable && (
                    <Button
                      variant="outlined"
                      startIcon={<Security />}
                      onClick={generateOTP}
                      disabled={otpLoading}
                      sx={{ 
                        borderRadius: 2,
                        borderColor: '#FF7700',
                        color: '#FF7700',
                        '&:hover': {
                          borderColor: '#E56900',
                          backgroundColor: 'rgba(255,119,0,0.05)',
                        }
                      }}
                    >
                      {otpLoading ? 'Sending OTP...' : 'Edit Details'}
                    </Button>
                  )}
                </Box>
                
                {accountDetailsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ ml: 2 }}>Loading account details...</Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Account Holder Name"
                        name="account_holder_name"
                        value={bankingEditable ? bankingDetails.account_holder_name : (formData.account_holder_name || '')}
                        onChange={bankingEditable ? handleBankingDetailsChange : handleInputChange}
                        disabled={!bankingEditable}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Account Number"
                        name="account_number"
                        value={bankingEditable ? bankingDetails.account_number : (formData.account_number || '')}
                        onChange={bankingEditable ? handleBankingDetailsChange : handleInputChange}
                        disabled={!bankingEditable}
                        required
                        error={Boolean(
                          bankingEditable &&
                            bankingDetails.account_number &&
                            bankingDetails.account_number.length < 6
                        )}
                        helperText={
                          bankingEditable &&
                          bankingDetails.account_number &&
                          bankingDetails.account_number.length < 6
                            ? "Enter complete account number"
                            : ""
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CreditCardIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    {bankingEditable && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Re-enter Account Number"
                          value={reEnterAccountNumber}
                          onChange={(e) => setReEnterAccountNumber(e.target.value)}
                          disabled={!bankingEditable}
                          required
                          error={Boolean(
                            reEnterAccountNumber &&
                              bankingDetails.account_number &&
                              reEnterAccountNumber !== bankingDetails.account_number
                          )}
                          helperText={
                            reEnterAccountNumber &&
                            bankingDetails.account_number &&
                            reEnterAccountNumber !== bankingDetails.account_number
                              ? "Account numbers do not match"
                              : ""
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CreditCardIcon color="primary" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Bank Name"
                        name="bank_name"
                        value={bankingEditable ? bankingDetails.bank_name : (formData.bank_name || '')}
                        onChange={bankingEditable ? handleBankingDetailsChange : handleInputChange}
                        disabled={!bankingEditable}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccountBalanceIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="IFSC Code"
                        name="ifsc_code"
                        value={bankingEditable ? bankingDetails.ifsc_code : (formData.ifsc_code || '')}
                        onChange={bankingEditable ? handleBankingDetailsChange : handleInputChange}
                        disabled={!bankingEditable}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="GST Number (Optional)"
                        name="gst_number"
                        value={bankingEditable ? bankingDetails.gst_number : (formData.gst_number || '')}
                        onChange={bankingEditable ? handleBankingDetailsChange : handleInputChange}
                        disabled={!bankingEditable || (bankingEditable && gstLocked)}
                        placeholder="Enter GST number (optional)"
                        InputProps={{
                          endAdornment: !bankingEditable
                            ? getGstFieldAdornment(bankingDetails.gst_active)
                            : (bankingEditable && gstVerifiedData
                              ? <InputAdornment position="end"><Chip icon={<CheckCircleIcon sx={{ fontSize: '16px !important' }} />} label="Verified" size="small" color="success" sx={{ fontWeight: 600, height: 24 }} /></InputAdornment>
                              : null),
                        }}
                        sx={
                          !bankingEditable && getGstStatusNormalized(bankingDetails.gst_active) === 'active'
                            ? {
                                '& .MuiInputBase-input.Mui-disabled': {
                                  WebkitTextFillColor: '#2e7d32',
                                  color: '#2e7d32',
                                  fontWeight: 600,
                                },
                              }
                            : undefined
                        }
                      />
                      {bankingEditable && !gstLocked && (
                        <Button
                          size="small"
                          onClick={handleVerifyGstSettings}
                          disabled={gstVerifying || !bankingDetails.gst_number?.trim()}
                          sx={{
                            mt: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600,
                            background: gstVerifying ? '#cbd5e1' : 'linear-gradient(135deg, #FF7700, #FF8800)',
                            color: '#000', '&:hover': { opacity: 0.9 },
                          }}
                        >
                          {gstVerifying ? <CircularProgress size={16} /> : 'Verify GST'}
                        </Button>
                      )}
                      {bankingEditable && gstLocked && (
                        <Button
                          size="small"
                          onClick={() => { setGstLocked(false); setGstVerifiedData(null); setBankingDetails(prev => ({ ...prev, gst_active: '' })); }}
                          sx={{ mt: 1, ml: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: '#64748b' }}
                          variant="outlined"
                        >
                          Edit
                        </Button>
                      )}
                      {bankingEditable && gstError && (
                        <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
                          <Typography variant="body2">{gstError}</Typography>
                        </Alert>
                      )}
                      {!bankingEditable && (
                        <GstVerificationStatus status={bankingDetails.gst_active} />
                      )}
                      {bankingEditable && gstVerifiedData && (
                        <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#14532d', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} /> GST Verified — Active
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={6}><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Legal Name</Typography><Typography variant="body2">{gstVerifiedData.legal_name || '—'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Trade Name</Typography><Typography variant="body2">{gstVerifiedData.trade_name || '—'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Constitution</Typography><Typography variant="body2">{gstVerifiedData.constitution || '—'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>State</Typography><Typography variant="body2">{gstVerifiedData.state || '—'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>District</Typography><Typography variant="body2">{gstVerifiedData.district || '—'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Pincode</Typography><Typography variant="body2">{gstVerifiedData.pincode || '—'}</Typography></Grid>
                          </Grid>
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="PAN Number"
                        name="pan_number"
                        value={bankingEditable ? bankingDetails.pan_number : (formData.pan_number || '')}
                        onChange={bankingEditable ? handleBankingDetailsChange : handleInputChange}
                        disabled={!bankingEditable || (bankingEditable && gstLocked)}
                        required
                        helperText={bankingEditable && gstLocked ? 'Auto-filled from GST verification' : ''}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Postal Code"
                        name="postal_code"
                        value={bankingEditable ? bankingDetails.postal_code : (formData.postal_code || '')}
                        onChange={bankingEditable ? handleBankingDetailsChange : handleInputChange}
                        disabled={!bankingEditable}
                        required
                      />
                    </Grid>
                  </Grid>
                )}
                
                {/* Banking Details Action Buttons */}
                {bankingEditable && (
                  <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={handleBankingDetailsCancel}
                      disabled={saveLoading}
                      sx={{ 
                        borderRadius: 2,
                        borderColor: 'rgba(0,0,0,0.2)',
                        color: '#2E4057',
                        '&:hover': {
                          borderColor: '#2E4057',
                          backgroundColor: 'rgba(46,64,87,0.05)',
                        }
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleBankingDetailsSave}
                      disabled={saveLoading}
                      startIcon={saveLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                      sx={{ 
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                        boxShadow: '0 4px 12px rgba(255,119,0,0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                          boxShadow: '0 6px 16px rgba(255,119,0,0.4)',
                        }
                      }}
                    >
                      {saveLoading ? "Saving..." : "Save Banking Details"}
                    </Button>
                  </Box>
                )}
              </Paper>

              {/* Account Proof Section */}
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                  <Security sx={{ mr: 1 }} />
                  Account Proof
                </Typography>
                
                <Grid container spacing={3}>
                  {/* Account Proof Upload */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2E4057', mb: 0.5 }}>
                        Account Proof
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                        (Cancel Cheque or Bank Statement or Passbook)
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="account-proof-input"
                        type="file"
                        onChange={handleAccountProofChange}
                      />
                      <label htmlFor="account-proof-input">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadFileIcon />}
                          fullWidth
                          sx={{
                            py: 1.5,
                            borderRadius: 2,
                            borderColor: '#FF7700',
                            color: '#FF7700',
                            '&:hover': {
                              borderColor: '#E56900',
                              backgroundColor: 'rgba(255,119,0,0.05)',
                            }
                          }}
                        >
                          Upload Account Proof 
                        </Button>
                      </label>
                    </Box>
                    {accountProofPreview && (
                      <Box sx={{ position: 'relative', mt: 2 }}>
                        <Box
                          component="img"
                          src={accountProofPreview}
                          alt="Account Proof Preview"
                          sx={{
                            width: '100%',
                            maxHeight: '300px',
                            objectFit: 'contain',
                            borderRadius: 2,
                            border: '2px solid rgba(255,119,0,0.2)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                        <IconButton
                          onClick={removeAccountProof}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(255,0,0,0.8)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(255,0,0,1)',
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Grid>

                  {/* ID Proof Upload */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2E4057', mb: 0.5 }}>
                        ID Proof
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                        (Any valid ID proof)
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="id-proof-input"
                        type="file"
                        onChange={handleIdProofChange}
                      />
                      <label htmlFor="id-proof-input">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadFileIcon />}
                          fullWidth
                          sx={{
                            py: 1.5,
                            borderRadius: 2,
                            borderColor: '#FF7700',
                            color: '#FF7700',
                            '&:hover': {
                              borderColor: '#E56900',
                              backgroundColor: 'rgba(255,119,0,0.05)',
                            }
                          }}
                        >
                          Upload ID Proof 
                        </Button>
                      </label>
                    </Box>
                    {idProofPreview && (
                      <Box sx={{ position: 'relative', mt: 2 }}>
                        <Box
                          component="img"
                          src={idProofPreview}
                          alt="ID Proof Preview"
                          sx={{
                            width: '100%',
                            maxHeight: '300px',
                            objectFit: 'contain',
                            borderRadius: 2,
                            border: '2px solid rgba(255,119,0,0.2)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                        <IconButton
                          onClick={removeIdProof}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(255,0,0,0.8)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(255,0,0,1)',
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Grid>
                </Grid>

                {/* Submit Button */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleAccountProofSubmit}
                    disabled={accountProofLoading || !accountProofFile || !idProofFile}
                    startIcon={accountProofLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    sx={{
                      borderRadius: 2,
                      px: 4,
                      py: 1.2,
                      background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                      boxShadow: '0 4px 12px rgba(255,119,0,0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                        boxShadow: '0 6px 16px rgba(255,119,0,0.4)',
                      },
                      '&:disabled': {
                        background: 'rgba(255,119,0,0.3)',
                        color: 'rgba(255,255,255,0.5)'
                      }
                    }}
                  >
                    {accountProofLoading ? 'Uploading...' : 'Add Account Proof'}
                  </Button>
                </Box>
              </Paper>
            </motion.div>
          )}

          {/* OTP Input Dialog */}
          <Dialog 
            open={showOtpInput} 
            onClose={() => !otpLoading && setShowOtpInput(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                border: '1px solid rgba(0,0,0,0.05)'
              }
            }}
          >
            <DialogTitle sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              color: '#2E4057',
              fontWeight: 600,
              pb: 1,
              borderBottom: '1px solid rgba(0,0,0,0.08)'
            }}>
              <Security sx={{ color: '#FF7700' }} />
              Security Verification
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ 
                  mb: 1, 
                  color: '#2E4057',
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}>
                  Verify Your Identity
                </Typography>
                <Typography variant="body2" sx={{ 
                  mb: 2, 
                  color: 'text.secondary',
                  lineHeight: 1.6
                }}>
                  To edit banking details, we need to verify your identity. We've sent a 6-digit verification code to:
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'rgba(255,119,0,0.05)', 
                  borderRadius: 2,
                  border: '1px solid rgba(255,119,0,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <EmailIcon sx={{ color: '#FF7700', fontSize: '1.2rem' }} />
                  <Typography variant="body1" sx={{ 
                    fontWeight: 500,
                    color: '#2E4057'
                  }}>
                    {formData.email}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ 
                  mb: 1.5,
                  color: 'text.secondary',
                  fontWeight: 500
                }}>
                  Enter Verification Code
                </Typography>
                <TextField
                  fullWidth
                  label="6-digit OTP"
                  value={otpInput}
                  onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={otpLoading}
                  placeholder="000000"
                  inputProps={{ 
                    maxLength: 6,
                    style: { 
                      textAlign: 'center',
                      fontSize: '1.2rem',
                      letterSpacing: '0.5rem',
                      fontWeight: 600
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#FF7700' },
                      '&.Mui-focused fieldset': { borderColor: '#FF7700', borderWidth: 2 }
                    },
                    '& .MuiInputLabel-root': {
                      color: 'text.secondary'
                    }
                  }}
                />
              </Box>
              
              <Typography variant="caption" sx={{ 
                color: 'text.secondary',
                display: 'block',
                textAlign: 'center',
                mt: 1
              }}>
                Didn't receive the code? Check your spam folder or request a new one.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ 
              p: 3, 
              gap: 2,
              borderTop: '1px solid rgba(0,0,0,0.08)',
              bgcolor: 'rgba(0,0,0,0.02)'
            }}>
              <Button
                onClick={() => setShowOtpInput(false)}
                disabled={otpLoading}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.05)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={verifyOTP}
                disabled={otpLoading || !otpInput.trim() || otpInput.length !== 6}
                variant="contained"
                sx={{ 
                  borderRadius: 2,
                  px: 4,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#FF7700',
                  '&:hover': {
                    bgcolor: '#E56900'
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255,119,0,0.3)',
                    color: 'rgba(255,255,255,0.5)'
                  }
                }}
              >
                {otpLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    Verifying...
                  </Box>
                ) : (
                  'Verify Code'
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Tab 5: Amenities */}
          {tabValue === 3 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              variants={itemVariants}
            >
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <WifiIcon sx={{ mr: 1 }} />
                  Amenities
                </Typography>
                <Autocomplete
                  multiple
                  id="amenities"
                  options={amenitiesList}
                  value={formData.amenities}
                  onChange={(event, newValue, reason) => {
                    handleAmenitiesChange(event, newValue);
                    if (reason === 'selectOption') {
                      setAmenitiesDropdownOpen(true);
                    } else if (reason === 'blur') {
                      setAmenitiesDropdownOpen(false);
                    }
                  }}
                  open={amenitiesDropdownOpen}
                  onOpen={() => setAmenitiesDropdownOpen(true)}
                  onClose={(event, reason) => {
                    if (reason === 'blur' || reason === 'escape' || reason === 'toggleInput') {
                      setAmenitiesDropdownOpen(false);
                    }
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li {...props} style={{ background: selected ? 'rgba(255,119,0,0.08)' : 'inherit', fontWeight: selected ? 600 : 400 }}>
                      <Chip
                        icon={getAmenityIcon(option)}
                        label={option}
                        color={selected ? 'primary' : 'default'}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {option}
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...otherProps } = getTagProps({ index });
                        return (
                      <Chip
                            key={key}
                        label={option}
                        icon={getAmenityIcon(option)}
                        color="primary"
                            {...otherProps}
                      />
                        );
                      })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Select Amenities"
                      placeholder="Add amenity"
                      onKeyDown={e => {
                        if (e.key === 'Enter') setAmenitiesDropdownOpen(false);
                      }}
                    />
                  )}
                />
                
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Selected Amenities:
                  </Typography>
                  <Grid container spacing={1}>
                    {formData.amenities.map((amenity) => (
                      <Grid item key={amenity}>
                        <Chip
                          icon={getAmenityIcon(amenity)}
                          label={amenity}
                          color="primary"
                          variant="outlined"
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>
            </motion.div>
          )}

          {/* Tab 6: Policies & Schedule */}
          {tabValue === 4 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              variants={itemVariants}
            >
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <LoyaltyIcon sx={{ mr: 1 }} />
                  Policies & Schedule
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Check-In Time"
                      name="check_in_time"
                      type="time"
                      value={formData.check_in_time}
                      onChange={handleCheckInOutChange}
                      disabled={checkInOutSaving}
                      helperText={checkInOutSaving ? 'Updating…' : 'Saves automatically when changed'}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccessTimeIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Check-Out Time"
                      name="check_out_time"
                      type="time"
                      value={formData.check_out_time}
                      onChange={handleCheckInOutChange}
                      disabled={checkInOutSaving}
                      helperText={checkInOutSaving ? 'Updating…' : 'Saves automatically when changed'}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccessTimeIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!formData.open_24_7}
                          onChange={handleInputChange}
                          name="open_24_7"
                          color="primary"
                        />
                      }
                      label="Open 24/7"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!formData.is_couple_allowed}
                          onChange={handleInputChange}
                          name="is_couple_allowed"
                          color="primary"
                        />
                      }
                      label="Couples Allowed"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Cancellation Policy"
                      name="cancellation_policy"
                      value={formData.cancellation_policy}
                      onChange={handleInputChange}
                      disabled={false}
                    >
                      {[
                        ...CANCELLATION_POLICY_OPTIONS,
                        ...(formData.cancellation_policy &&
                        !CANCELLATION_POLICY_OPTIONS.includes(formData.cancellation_policy)
                          ? [formData.cancellation_policy]
                          : []),
                      ].map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={HOTEL_POLICY_OPTIONS}
                      value={splitPolicyList(formData.hotel_policy)}
                      onChange={(_, newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          hotel_policy: (newValue || []).map((v) => String(v).trim()).filter(Boolean).join(', '),
                        }));
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={`${option}-${index}`}
                            label={option}
                            size="small"
                            sx={{ backgroundColor: 'rgba(255,119,0,0.12)', color: '#FF7700' }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Hotel Policy"
                          placeholder="Select or type policies"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                
                {/* Hotel Time Save Button */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    variant="contained"
                    color="primary"
                    type="button"
                    startIcon={hotelTimeLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    disabled={hotelTimeLoading}
                    onClick={handleSaveHotelTime}
                    sx={{ 
                      px: 4,
                      py: 1.2,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                      boxShadow: '0 4px 12px rgba(255,119,0,0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                        boxShadow: '0 6px 16px rgba(255,119,0,0.4)',
                      }
                    }}
                  >
                    {hotelTimeLoading ? "Saving..." : "Save Policies"}
                  </Button>
                </Box>
              </Paper>
            </motion.div>
          )}

          {/* Tab 7: Assign User / Sub Host Management */}
          {tabValue === 5 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              variants={itemVariants}
            >
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <PeopleIcon sx={{ mr: 1 }} />
                  Assign User / Sub Host Management
                </Typography>
                
                {/* Existing Sub Hosts */}
                {allSubHosts.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2E4057' }}>
                      Existing Sub Hosts
                    </Typography>
                    <Grid container spacing={2}>
                      {allSubHosts.map((subHost) => {
                        const getAccessLabel = () => {
                          if (subHost.inventory_manage === '1' || subHost.inventory_manage === 1) return 'Inventory Manage';
                          if (subHost.booking_manage === '1' || subHost.booking_manage === 1) return 'Booking Manage';
                          if (subHost.hotel_manage === '1' || subHost.hotel_manage === 1) return 'Hotel Manage';
                          if (subHost.view_only === '1' || subHost.view_only === 1) return 'View Only';
                          return 'No Access';
                        };
                        
                        const getAccessColor = () => {
                          if (subHost.inventory_manage === '1' || subHost.inventory_manage === 1) return '#4CAF50';
                          if (subHost.booking_manage === '1' || subHost.booking_manage === 1) return '#2196F3';
                          if (subHost.hotel_manage === '1' || subHost.hotel_manage === 1) return '#FF7700';
                          if (subHost.view_only === '1' || subHost.view_only === 1) return '#9E9E9E';
                          return '#F44336';
                        };
                        
                        return (
                          <Grid item xs={12} sm={6} md={4} key={subHost.id}>
                            <Card
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid rgba(0,0,0,0.1)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                  transform: 'translateY(-2px)'
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <PersonIcon sx={{ color: '#FF7700', mr: 1, fontSize: '1.5rem' }} />
                                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#2E4057' }}>
                                  {subHost.name}
                                </Typography>
                              </Box>
                              <Box sx={{ mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                  <EmailIcon sx={{ fontSize: '0.9rem', color: 'text.secondary', mr: 0.5 }} />
                                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                    {subHost.email}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <PhoneIcon sx={{ fontSize: '0.9rem', color: 'text.secondary', mr: 0.5 }} />
                                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                    {subHost.phone}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                                <Chip
                                  label={getAccessLabel()}
                                  size="small"
                                  sx={{
                                    backgroundColor: getAccessColor(),
                                    color: 'white',
                                    fontWeight: 500,
                                    fontSize: '0.75rem'
                                  }}
                                />
                              </Box>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
                
                <Divider sx={{ my: 3 }} />
                
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2E4057' }}>
                  Add New Sub Host
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={subHostFormData.name}
                      onChange={(e) => setSubHostFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={subHostFormData.email}
                      onChange={(e) => setSubHostFormData(prev => ({ ...prev, email: e.target.value }))}
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      value={subHostFormData.phone}
                      onChange={(e) => setSubHostFormData(prev => ({ ...prev, phone: e.target.value }))}
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Gender"
                      name="gender"
                      value={subHostFormData.gender}
                      onChange={(e) => setSubHostFormData(prev => ({ ...prev, gender: e.target.value }))}
                      required
                    >
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={subHostFormData.password}
                      onChange={(e) => setSubHostFormData(prev => ({ ...prev, password: e.target.value }))}
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Confirm Password"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={subHostFormData.confirmPassword}
                      onChange={(e) => setSubHostFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                      error={Boolean(
                        subHostFormData.confirmPassword &&
                          subHostFormData.password !== subHostFormData.confirmPassword
                      )}
                      helperText={
                        subHostFormData.confirmPassword &&
                        subHostFormData.password !== subHostFormData.confirmPassword
                          ? "Passwords do not match"
                          : ""
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Role / Permission</InputLabel>
                      <Select
                        value={subHostFormData.role}
                        label="Role / Permission"
                        onChange={(e) => setSubHostFormData(prev => ({ ...prev, role: e.target.value }))}
                      >
                        <MenuItem value="inventory_manage">Inventory Manage</MenuItem>
                        <MenuItem value="booking_manage">Booking Manage</MenuItem>
                        <MenuItem value="hotel_manage">Hotel Manage</MenuItem>
                        <MenuItem value="view_only">View Only</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                
                {/* Submit Button */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    variant="contained"
                    color="primary"
                    type="button"
                    startIcon={subHostLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    disabled={subHostLoading}
                    onClick={handleSubHostSubmit}
                    sx={{ 
                      px: 4,
                      py: 1.2,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                      boxShadow: '0 4px 12px rgba(255,119,0,0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                        boxShadow: '0 6px 16px rgba(255,119,0,0.4)',
                      }
                    }}
                  >
                    {subHostLoading ? "Submitting..." : "Assign User"}
                  </Button>
                </Box>
              </Paper>
            </motion.div>
          )}

          </Box>

          {/* Action Buttons - Only show for Amenities section */}
          {tabValue === 3 && (
          <motion.div 
            variants={itemVariants}
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginTop: '2rem',
              gap: '1rem'
            }}
          >
            <Button
              component={motion.button}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              variant="outlined"
              color="secondary"
              startIcon={<CancelIcon />}
              onClick={() => navigate('/')}
              sx={{ 
                px: 4,
                py: 1.2,
                borderRadius: '12px',
                borderColor: 'rgba(0,0,0,0.2)',
                color: '#2E4057',
                '&:hover': {
                  borderColor: '#2E4057',
                  backgroundColor: 'rgba(46,64,87,0.05)',
                }
              }}
            >
              Cancel
            </Button>
            <Button
              component={motion.button}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              variant="contained"
              color="primary"
              startIcon={saveLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={saveLoading}
              sx={{ 
                px: 5,
                py: 1.2,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                boxShadow: '0 4px 12px rgba(255,119,0,0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                  boxShadow: '0 6px 16px rgba(255,119,0,0.4)',
                }
              }}
            >
              {saveLoading ? "Saving..." : "Save Changes"}
            </Button>
          </motion.div>
          )}
        </form>
      </motion.div>

      {/* Snackbar with updated styling */}
      <Snackbar 
        open={alertOpen} 
        autoHideDuration={6000} 
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleAlertClose} 
          severity={alertSeverity} 
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            '& .MuiAlert-icon': {
              fontSize: '24px'
            }
          }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Admin Access Button with updated styling */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          opacity: 0.2,
          '&:hover': {
            opacity: 0.8
          },
          transition: 'all 0.3s ease',
          zIndex: 1000
        }}
      >
        <IconButton
          size="small"
          onClick={handleAdminAccess}
          sx={{
            width: '32px',
            height: '32px',
            backgroundColor: '#2E4057',
            '&:hover': {
              backgroundColor: '#FF7700',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.3s ease',
            '& .MuiSvgIcon-root': {
              fontSize: '20px',
              color: '#fff'
            }
          }}
        >
          <AdminPanelSettingsIcon />
        </IconButton>
      </Box>

    </Container>
  );
};

// Helper function to determine which icon to use for each amenity
const getAmenityIcon = (amenity) => {
  switch(amenity) {
    case 'Swimming Pool': return <PoolIcon fontSize="small" />;
    case 'Wifi': return <WifiIcon fontSize="small" />;
    case 'Air Conditioning': return <AcUnitIcon fontSize="small" />;
    case 'Parking': return <LocalParkingIcon fontSize="small" />;
    case 'Room Service': return <RoomServiceIcon fontSize="small" />;
    case 'Laundry': return <LocalLaundryServiceIcon fontSize="small" />;
    case 'Restaurant': return <RestaurantIcon fontSize="small" />;
    case 'Gym/Fitness Centre': return <FitnessCenterIcon fontSize="small" />;
    default: return <KeyboardArrowRightIcon fontSize="small" />;
  }
};

// Settings icon component
const SettingIcon = (props) => {
  return (
    <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" fill="currentColor" />
    </svg>
  );
};

// Check icon for amenities
const CheckIcon = (props) => {
  return (
    <svg {...props} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor" />
    </svg>
  );
};

export default Settings;